import { useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { markAllNotificationsAsRead, markNotificationAsRead } from "api/services/user";
import { userQueryKeys } from "./userQueryKeys";
import type { RecentUpdateNotification } from "./useGetRecentUpdatesData";

const recentUpdatesQueryKey = [userQueryKeys.recentUpdates];

const markReadInCache = (notifications: RecentUpdateNotification[] | undefined, ids?: Set<number>) =>
  notifications?.map((notification) =>
    !ids || ids.has(notification.id) ? { ...notification, unread: false, is_read: true } : notification,
  );

const snapshotRecentUpdates = async (queryClient: QueryClient) => {
  await queryClient.cancelQueries({ queryKey: recentUpdatesQueryKey });
  return queryClient.getQueryData<RecentUpdateNotification[]>(recentUpdatesQueryKey);
};

const restoreRecentUpdates = (queryClient: QueryClient, previous?: RecentUpdateNotification[]) => {
  if (previous) queryClient.setQueryData(recentUpdatesQueryKey, previous);
};

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => markNotificationAsRead(notificationId),
    onMutate: async (notificationId) => {
      const previousNotifications = await snapshotRecentUpdates(queryClient);
      queryClient.setQueryData<RecentUpdateNotification[]>(recentUpdatesQueryKey, (notifications) =>
        markReadInCache(notifications, new Set([notificationId])),
      );
      return { previousNotifications };
    },
    onError: (_error, _notificationId, context) => restoreRecentUpdates(queryClient, context?.previousNotifications),
    onSettled: () => queryClient.invalidateQueries({ queryKey: recentUpdatesQueryKey }),
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsAsRead(),
    onMutate: async () => {
      const previousNotifications = await snapshotRecentUpdates(queryClient);
      queryClient.setQueryData<RecentUpdateNotification[]>(recentUpdatesQueryKey, (notifications) =>
        markReadInCache(notifications),
      );
      return { previousNotifications };
    },
    onError: (_error, _variables, context) => restoreRecentUpdates(queryClient, context?.previousNotifications),
    onSettled: () => queryClient.invalidateQueries({ queryKey: recentUpdatesQueryKey }),
  });
}
